<template>
    <Transition name="thread-panel" :css="drawerOpen || drawerLeaveAnimated">
        <aside
            v-if="drawerOpen"
            class="thread-panel"
            data-testid="thread-panel"
        >
            <!-- Every view gets a header. The detail view lost its one when
                 title/status/actions moved to the draft bar, which left the
                 panel with nothing saying what it was and no way to shut it
                 but the bar's own toggle. Title, the Auto switch and close —
                 the draft's actions stay on the bar. -->
            <div class="thread-panel-header">
                <span class="thread-panel-title">{{ drawerHeader }}</span>
                <div class="thread-panel-header-actions">
                    <!-- Auto-apply sits with the conversation it governs: how
                         far the agent's proposals are trusted is decided where
                         they are read. Only for an open draft — a discussion
                         stages nothing, so there is nothing to apply. -->
                    <label
                        v-if="showAutoApply"
                        class="auto-toggle"
                        v-tooltip.bottom="
                            'Add suggested changes to this draft automatically, without asking each time. Nothing reaches your business until you apply the draft.'
                        "
                    >
                        <ToggleSwitch
                            :modelValue="activeThread?.auto_apply"
                            data-testid="drawer-auto-apply"
                            @update:modelValue="setAutoApply"
                        />
                        <span>Auto</span>
                    </label>
                    <Button
                        icon="pi pi-times"
                        text
                        rounded
                        size="small"
                        :aria-label="view === 'create' ? 'Back' : 'Close'"
                        data-testid="thread-panel-close"
                        @click="closePanel"
                    />
                </div>
            </div>

            <div class="thread-panel-body">
                <!-- Inline create form -->
                <template v-if="view === 'create'">
                    <div class="drawer-form">
                        <div class="field">
                            <label for="drawer-thread-title">Title</label>
                            <InputText
                                id="drawer-thread-title"
                                v-model="createTitle"
                                placeholder="What are you changing?"
                                class="w-full"
                                @keydown.enter="createNewThread"
                            />
                        </div>
                        <div class="field">
                            <label for="drawer-thread-desc"
                                >Description (optional)</label
                            >
                            <Textarea
                                id="drawer-thread-desc"
                                v-model="createDescription"
                                placeholder="Provide more context..."
                                rows="3"
                                class="w-full"
                            />
                        </div>
                        <div class="drawer-form-actions">
                            <Button
                                label="Cancel"
                                text
                                size="small"
                                @click="view = 'list'"
                            />
                            <Button
                                label="Create"
                                icon="pi pi-plus"
                                size="small"
                                :loading="isCreating"
                                :disabled="!createTitle.trim()"
                                @click="createNewThread"
                            />
                        </div>
                    </div>
                </template>

                <template v-else-if="view === 'detail' && activeThread">
                    <ThreadConversation
                        v-if="drawerThreadId"
                        :thread-id="drawerThreadId"
                    />
                </template>

                <!-- Loading state for thread detail -->
                <template v-else-if="view === 'detail' && isFetching">
                    <div class="drawer-loading">
                        <ProgressSpinner />
                    </div>
                </template>

                <!-- Drafts list view — "New Draft" lives in the panel footer
                     (below), out of the way of the list itself. -->
                <template v-else>
                    <!-- Only when there is genuinely nothing to show. This sat
                         above the list unconditionally, so every open unmounted
                         an already-correct list and spun instead — and
                         `list_threads` re-diffs every open draft, so that is
                         tens of seconds of hiding data the client already holds.
                         The refresh still runs; it just happens behind the
                         list now. -->
                    <div
                        v-if="isFetchingList && threads.length === 0"
                        class="drawer-loading"
                    >
                        <ProgressSpinner />
                    </div>

                    <div v-else-if="threads.length === 0" class="drawer-empty">
                        <i
                            class="pi pi-comments"
                            style="font-size: 2rem; opacity: 0.3"
                        />
                        <p>No drafts yet.</p>
                        <p class="muted">
                            Start one to work on changes before they reach your
                            business.
                        </p>
                    </div>

                    <div v-else class="drawer-threads-list">
                        <template
                            v-for="section in threadSections"
                            :key="section.key"
                        >
                            <div
                                v-if="section.label"
                                class="drawer-section-header"
                                :class="{
                                    'is-secondary': section.key === 'other',
                                }"
                                :data-testid="`thread-section-${section.key}`"
                            >
                                {{ section.label }}
                            </div>
                            <div
                                v-for="thread in section.threads"
                                :key="thread.identity"
                                class="drawer-thread-card"
                                :class="{
                                    'is-viewing':
                                        thread.identity ===
                                        releaseStore.activeThreadId,
                                    'is-secondary': section.key === 'other',
                                }"
                                data-testid="thread-card"
                                @click="openThread(thread)"
                            >
                                <div class="drawer-card-top">
                                    <span class="drawer-card-title">{{
                                        thread.title
                                    }}</span>
                                    <span class="drawer-card-top-right">
                                        <span
                                            v-if="
                                                thread.identity ===
                                                releaseStore.activeThreadId
                                            "
                                            class="viewing-hint"
                                            title="You are viewing this draft"
                                        >
                                            <i class="pi pi-eye" />
                                            Viewing
                                        </span>
                                        <button
                                            type="button"
                                            class="drawer-card-edit"
                                            :class="{
                                                'drawer-card-edit--disabled':
                                                    !isOwnDraft(thread),
                                            }"
                                            :aria-disabled="!isOwnDraft(thread)"
                                            :title="`Edit ${thread.title}`"
                                            :aria-label="`Edit ${thread.title}`"
                                            v-tooltip.top="
                                                renameBlocked(thread)
                                            "
                                            data-testid="thread-card-edit-details"
                                            @click.stop="
                                                editDraftDetails(thread)
                                            "
                                        >
                                            <i class="pi pi-pencil" />
                                        </button>
                                        <button
                                            v-if="
                                                isOwnDraft(thread) &&
                                                !isViewOnly
                                            "
                                            type="button"
                                            class="drawer-card-discard"
                                            :title="`Discard ${thread.title}`"
                                            :aria-label="`Discard ${thread.title}`"
                                            data-testid="thread-card-discard"
                                            @click.stop="discardThread(thread)"
                                        >
                                            <i class="pi pi-trash" />
                                        </button>
                                    </span>
                                </div>
                                <div class="drawer-card-meta">
                                    <span
                                        v-if="thread.created_at"
                                        :title="
                                            formatExactTime(thread.created_at)
                                        "
                                    >
                                        started
                                        {{
                                            formatRelativeTime(
                                                thread.created_at
                                            )
                                        }}
                                    </span>
                                    <span
                                        v-if="thread.unread_count"
                                        class="drawer-card-unread"
                                        data-testid="thread-card-unread"
                                        >&middot;
                                        {{ thread.unread_count }} new</span
                                    >
                                    <span
                                        class="drawer-card-status"
                                        :class="`is-${thread.status}`"
                                    >
                                        &middot;
                                        {{ threadStatusLabel(thread.status) }}
                                    </span>
                                </div>
                                <div
                                    v-if="thread.description"
                                    class="drawer-card-desc"
                                >
                                    {{ thread.description }}
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </div>

            <!-- Starting a draft is the one thing you do FROM this list rather
                 than to something in it, so it sits under the list as a full
                 width action instead of competing with the title. Footer, not
                 header: the list is what you came for, and the button stays
                 put while it scrolls. -->
            <div
                v-if="view === 'list' && !isViewOnly"
                class="thread-panel-footer"
            >
                <Button
                    icon="pi pi-plus"
                    label="New Draft"
                    severity="secondary"
                    size="small"
                    class="w-full"
                    :loading="isLoading"
                    data-testid="thread-new"
                    @click="startCreate"
                />
            </div>
        </aside>
    </Transition>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import Textarea from "primevue/textarea";
import ToggleSwitch from "primevue/toggleswitch";
import { useDialog } from "primevue/usedialog";
import { computed, onUnmounted, ref, watch } from "vue";

import EditDraftDetailsDialog from "@/components/Thread/EditDraftDetailsDialog.vue";
import ResolveThreadDialog from "@/components/Thread/ResolveThreadDialog.vue";
import ThreadConversation from "@/components/Thread/ThreadConversation.vue";
import {
    formatExactTime,
    formatRelativeTime,
} from "@/components/Thread/thread-format";
import {
    type ThreadSection,
    groupThreadsByPage,
} from "@/components/Thread/thread-sections";
import {
    isOwnOpenDraft,
    renameBlockedReason,
    threadStatusLabel,
} from "@/components/Thread/thread-status";
import { useEditManager } from "@/composables/edit-manager";
import { useThreadBranchView } from "@/composables/use-thread-branch-view";
import { isDraftThread } from "@/lib/draft-thread";
import { onEntityChange } from "@/stores/cache-invalidation";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";
import { type ThreadSchema, useThreadsStore } from "@/stores/threads-store";
import { useUserManager } from "@/stores/user-store";

const store = useThreadsStore();

const dialog = useDialog();

const releaseStore = useReleaseStore();
const { user } = useUserManager();
// Viewers read drafts; creating, discarding and changing them is refused.
const { isViewOnly } = useEditManager();
const { enterDraft, exitThreadBranchView, drainPendingEdits, leaveIfResolved } =
    useThreadBranchView();

// The entity shown on the current page — used to split the list into threads
// "On this page" vs "On other pages".
const { identity: currentEntityIdentity } = useCurrentEntityManager();

const {
    threads,
    activeThread,
    isLoading,
    isFetching,
    isFetchingList,
    drawerOpen,
    drawerThreadId,
    drawerLeaveAnimated,
} = storeToRefs(store);

// Internal view state: 'list' | 'detail' | 'create'
const view = ref<"list" | "detail" | "create">("list");

// Identities of threads that affect the current page's entity (open threads, via
// the store's entity → threads map). Resolved threads carry no affected entities,
// so they always fall into "On other pages".
const onThisPageIds = computed(() => {
    const id = currentEntityIdentity.value;
    if (!id || id === "unknown") return new Set<string>();
    return new Set(store.getThreadsForEntity(id).map((t) => t.identity));
});

// Drafts are the kind-less threads that stage changes. A `kind` marks an
// agent thread (onboarding, market research, BPMN generation…), and
// `is_discussion` a conversation about the working copy — neither is a place
// the user put changes, so neither belongs under a heading that offers to
// discard them alongside work in progress.
const draftThreads = computed(() => threads.value.filter(isDraftThread));

const threadSections = computed<ThreadSection[]>(() =>
    groupThreadsByPage(draftThreads.value, onThisPageIds.value)
);

// Create form state
const createTitle = ref("");
const createDescription = ref("");
const isCreating = ref(false);

// Accept/Reject live in the draft bar (ApplicationLayout's DraftBar), so the
// detail header names the draft rather than repeating its actions.
const drawerHeader = computed(() => {
    if (view.value === "create") return "New Draft";
    if (view.value === "detail") return activeThread.value?.title ?? "Draft";
    return "Drafts";
});

// Same gate as entering the draft view: an open, branch-backed thread that is
// not a discussion. Resolved drafts and discussions have nothing to auto-apply,
// and a viewer may not change the draft at all.
const showAutoApply = computed(
    () =>
        !isViewOnly.value &&
        view.value === "detail" &&
        activeThread.value?.status === "open" &&
        !!activeThread.value.branch &&
        !activeThread.value.is_discussion
);

// Named explicitly rather than left to the store's `activeThread` fallback, so
// the switch can never land on a thread other than the one this panel shows.
const setAutoApply = (enabled: boolean) => {
    if (!drawerThreadId.value) return;
    void store.setAutoApply(enabled, drawerThreadId.value);
};

// Sync view state with drawerThreadId and activate thread branch
watch(
    drawerThreadId,
    async (threadId) => {
        if (threadId) {
            // A panel on its way out stays on the list it was showing: the
            // conversation would only flash for the length of the close
            // animation before the draft bar took over.
            if (drawerOpen.value) view.value = "detail";
            const landed = await store.fetchThread(threadId);
            if (!landed || drawerThreadId.value !== threadId) return;
            // Track the open thread in the URL (?thread=) so it survives reloads.
            // Resolved threads keep their (now-deleted) branch string forever —
            // resolve_thread.py never clears it — so without the status check a
            // history card would activate the branch view and stamp ?thread=
            // over a draft that can no longer be applied or left-and-kept.
            //
            // A discussion is skipped: it has a branch like everything else,
            // but it is a conversation ABOUT the working copy, so entering it
            // would point every read at a branch nobody is staging on and
            // raise a draft bar over a thread with nothing to apply. That is
            // the whole reason the flag exists — asking a question used to
            // mean being pulled into a draft.
            if (
                activeThread.value?.branch &&
                activeThread.value?.status === "open" &&
                !activeThread.value?.is_discussion
            ) {
                await enterDraft(threadId);
            } else if (releaseStore.activeThreadId === threadId) {
                // A card enters its draft on the list's word, and the list
                // carries no branch and can be stale about status. This
                // record decides: back out of what it says was not enterable.
                await exitThreadBranchView();
            }
        } else {
            view.value = "list";
        }
    },
    { immediate: true }
);

// Fetch threads when drawer opens, reset view when it closes.
//
// Without the diffs: this refresh exists to notice drafts that appeared or were
// resolved elsewhere, and the records alone say that. Asking for
// `affected_entities` as well would make opening the drawer wait on a worktree
// build per open draft — tens of seconds — to decide which of two headings each
// card sits under. The page-load fetch still asks for them, and a diff-less
// response never clears the ones we already hold, so the grouping and the
// sidebar badges keep working.
watch(drawerOpen, async (open) => {
    if (open) {
        view.value = drawerThreadId.value ? "detail" : "list";
        await store.fetchThreads({ withDiffs: false });
    }
});

// Open an SSE connection for the active thread so all viewers see contributions and
// agent activity in real time (replaces background polling).
watch(
    [drawerOpen, drawerThreadId],
    ([open, threadId]) => {
        if (open && threadId) {
            void store.connectToThread(threadId);
            // Open/view catch-up trigger: bring the branch up to date with main on
            // open so the viewer sees fresh content + diff badges and any behind-main
            // conflicts surface. Best-effort; refetches + re-syncs once caught up.
            void store.catchUpThread(threadId);
        } else {
            store.disconnectThread();
        }
    },
    { immediate: true }
);

// Surface the non-agent (user) branch edits in the detail view so a reviewer
// sees what they changed directly alongside the agent's work. Fetched when a
// thread is opened and refreshed whenever an entity changes — so the user's own
// direct edit (a silent branch write that creates no thread entry) shows up
// without needing to reopen the thread.
const refreshUserChanges = () => {
    if (drawerThreadId.value) void store.fetchUserChanges(drawerThreadId.value);
};
watch(drawerThreadId, refreshUserChanges, { immediate: true });
const stopUserChangeRefresh = onEntityChange(
    () => releaseStore.viewingThreadBranch,
    refreshUserChanges
);
onUnmounted(() => stopUserChangeRefresh());

onUnmounted(() => {
    store.disconnectThread();
});

// From the create form, the × means "I'm not making one after all" — that
// belongs back on the list it was opened from, not shutting the panel.
const closePanel = () => {
    if (view.value === "create") {
        view.value = "list";
        return;
    }
    store.closeDrawer();
};

const discardThread = async (thread: ThreadSchema) => {
    if (releaseStore.activeThreadId === thread.identity) {
        await drainPendingEdits();
    }
    dialog.open(ResolveThreadDialog, {
        props: {
            header: thread.is_discussion
                ? "Discard discussion"
                : "Discard draft",
            modal: true,
            style: { width: "450px" },
        },
        data: { action: "reject", threadId: thread.identity },
        onClose: () => leaveIfResolved(thread.identity),
    });
};

const isOwnDraft = (thread: ThreadSchema) =>
    isOwnOpenDraft(thread, user.value?.identity);

const renameBlocked = (thread: ThreadSchema) =>
    renameBlockedReason(thread, user.value?.identity);

const editDraftDetails = (thread: ThreadSchema) => {
    if (!isOwnDraft(thread)) return;
    dialog.open(EditDraftDetailsDialog, {
        props: {
            header: "Draft details",
            modal: true,
            style: { width: "30rem" },
        },
        data: { thread },
    });
};

const openThread = async (thread: ThreadSchema) => {
    // Clicking the card of the draft you are already in moves you nowhere, so
    // there is nothing to get out of the way of — the click can only have
    // meant "show me this one's conversation".
    const entering =
        releaseStore.activeThreadId !== thread.identity &&
        thread.status === "open" &&
        !thread.is_discussion;
    if (!entering) {
        store.selectThread(thread.identity);
        return;
    }
    // Picking a draft means "take me into it": the panel is 420px of the page
    // the reader just chose to work on, so it goes at once, and the draft bar
    // comes up off the list record rather than behind the detail fetch. The
    // watcher above still lets that fetch overrule this.
    store.closeDrawer();
    await enterDraft(thread.identity);
    store.selectThread(thread.identity);
    void store.catchUpThread(thread.identity);
};

const startCreate = () => {
    createTitle.value = "";
    createDescription.value = "";
    view.value = "create";
};

const createNewThread = async () => {
    if (!createTitle.value.trim()) return;
    isCreating.value = true;
    const data = await store.createThread(createTitle.value.trim(), {
        description: createDescription.value.trim() || undefined,
    });
    isCreating.value = false;
    if (data) {
        store.closeDrawer();
        if (data.status === "open" && data.branch && !data.is_discussion) {
            await enterDraft(data.identity);
        }
        store.selectThread(data.identity);
    }
};
</script>

<style scoped>
/* In-layout right panel — a real column in the layout's `.content-row` (see
   ApplicationLayout.vue) rather than a floating overlay, so opening it reflows
   the page content narrower instead of covering it. It is a sibling of the
   scrolling `.main-container` and stretches to the same height, which is
   already the space left under this mode's bars. */
.thread-panel {
    flex: 0 0 420px;
    width: 420px;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--p-content-background);
    border-left: 1px solid var(--surface-border, #dee2e6);
}

/* PrimeVue's surface scale is intentionally overridden here because thread
   content uses these tokens for bubbles, cards, code blocks, and the composer.
   Keeping the override scoped avoids changing unrelated screens while making
   every thread surface follow the application's dark-mode palette. */
html.dark-mode .thread-panel {
    --p-content-background: #222222;
    --p-surface-0: #222222;
    --p-surface-50: #292929;
    --p-surface-100: #333333;
    --p-surface-200: #4a4a4a;
    --p-content-border-color: #5a5a5a;
    --surface-border: #5a5a5a;
    --p-text-muted-color: #c2c2c2;
    --p-text-secondary-color: #dddddd;
}

html.dark-mode .thread-panel :deep(.agent-bubble) {
    --p-primary-50: #382255;
    --p-primary-100: #633a91;
}

.thread-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 3rem;
    padding: 0.5rem 0.75rem 0.5rem 1rem;
    border-bottom: 1px solid var(--p-surface-200);
}

.thread-panel-header-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
}

/* Reads as a setting beside the close button, not as another button. */
.auto-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-right: 0.35rem;
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    cursor: pointer;
}

.thread-panel-title {
    font-weight: 600;
    font-size: 1.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Quiet until the card is hovered — a destructive action should not be the
   first thing the eye lands on in a list used mostly for switching. */
.drawer-card-discard,
.drawer-card-edit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: none;
    background: none;
    padding: 0.15rem 0.3rem;
    cursor: pointer;
    color: var(--p-text-muted-color);
    font-size: 0.8rem;
    opacity: 0;
}

.drawer-card-discard .pi,
.drawer-card-edit .pi {
    font-size: 1em;
    line-height: 1;
}

.drawer-card-discard {
    transition: opacity 0.12s ease-out;
}

.drawer-card-edit {
    transition:
        opacity 0.12s ease-out,
        color 0.12s ease-out;
}

.drawer-card-edit:hover,
.drawer-card-edit:focus-visible {
    color: var(--p-primary-color);
}

.drawer-card-discard:hover,
.drawer-card-discard:focus-visible {
    color: var(--p-red-500, #ef4444);
}

.drawer-thread-card:hover .drawer-card-discard,
.drawer-thread-card:hover .drawer-card-edit,
.drawer-card-edit:focus-visible,
.drawer-card-discard:focus-visible {
    opacity: 1;
}

.drawer-card-edit--disabled {
    cursor: default;
}

.drawer-thread-card:hover .drawer-card-edit--disabled,
.drawer-card-edit--disabled:focus-visible {
    opacity: 0.35;
}

.drawer-card-edit--disabled:hover,
.drawer-card-edit--disabled:focus-visible {
    color: var(--p-text-muted-color);
}

/* Mirrors the header's border so the list is framed top and bottom, and
   keeps its own padding — the body's 1.25rem would push the button off the
   panel's edges. */
.thread-panel-footer {
    flex-shrink: 0;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid var(--p-surface-200);
}

.thread-panel-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
}

/* Open/close is a subtle fade + slide; the column's width appears/disappears
   with v-if (reflowing the page), while its content eases in. */
.thread-panel-enter-active,
.thread-panel-leave-active {
    transition:
        opacity 160ms ease,
        transform 160ms ease;
}

.thread-panel-enter-from,
.thread-panel-leave-to {
    opacity: 0;
    transform: translateX(16px);
}

.drawer-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.field label {
    font-weight: 500;
    font-size: 0.9rem;
}

.drawer-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

/* Centred in the body, both axes — a spinner pinned to the top of an empty
   panel reads as a stuck header rather than a loading list. */
.drawer-loading {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-height: 12rem;
}

.drawer-empty {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--p-text-muted-color);
}

.drawer-empty p {
    margin: 0.25rem 0;
}

.drawer-empty .muted {
    font-size: 0.85rem;
    opacity: 0.7;
}

.drawer-threads-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.drawer-section-header {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--p-text-muted-color);
    padding: 0.25rem 0.1rem;
    margin-top: 0.25rem;
}

/* No top margin for the very first section header so it sits flush at the top of
   the list. */
.drawer-threads-list > .drawer-section-header:first-child {
    margin-top: 0;
}

/* De-emphasise the "On other pages" group so focus lands on the threads that
   relate to the current page. Kept subtle (not a "disabled" grey): the cards
   stay full-colour, just dialled back, and hovering restores full prominence so
   they still read as interactive. The section label carries the meaning, so this
   is a reinforcement, not the sole cue. */
.drawer-section-header.is-secondary {
    opacity: 0.65;
}

.drawer-thread-card.is-secondary {
    opacity: 0.6;
    transition:
        opacity 0.15s,
        border-color 0.15s;
}

.drawer-thread-card.is-secondary:hover {
    opacity: 1;
}

/* The branch currently being viewed always stays at full prominence, even if it
   happens to fall under "On other pages". */
.drawer-thread-card.is-secondary.is-viewing {
    opacity: 1;
}

.drawer-thread-card {
    border: 1px solid var(--p-surface-200);
    border-radius: 8px;
    padding: 0.75rem;
    cursor: pointer;
    transition: border-color 0.15s;
}

.drawer-thread-card:hover {
    border-color: var(--p-primary-color);
}

/* The thread whose branch is currently being viewed (?thread=): a subtle accent
   so it stands out in the list without shouting. */
.drawer-thread-card.is-viewing {
    border-color: var(--p-primary-color);
    border-left-width: 3px;
    background: color-mix(in srgb, var(--p-primary-color) 6%, transparent);
}

.drawer-card-top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.drawer-card-top-right {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;
}

/* Status as coloured text, not a chip. A filled tag per row gave the list a
   column of boxes competing with the titles, and its box height never sat
   right against them. */
.drawer-card-status {
    font-weight: 600;
    white-space: nowrap;
}

.drawer-card-status.is-open {
    color: var(--p-primary-color);
}

.drawer-card-status.is-accepted {
    color: var(--p-green-600, #16a34a);
}

.drawer-card-status.is-rejected {
    color: var(--p-red-500, #ef4444);
}

.viewing-hint {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--p-primary-color);
    white-space: nowrap;
}

.viewing-hint .pi {
    font-size: 0.7rem;
}

.drawer-card-title {
    font-weight: 600;
    font-size: 0.95rem;
}

.drawer-card-meta {
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.drawer-card-unread {
    color: var(--p-primary-color);
    font-weight: 600;
}

.drawer-card-desc {
    margin-top: 0.35rem;
    font-size: 0.85rem;
    color: var(--p-text-secondary-color);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
